import React, {useEffect, useMemo, useState} from "react";
import leftArrow from "../assets/icons/left-arrow.svg";
import {useNavigate} from "react-router-dom";
import MainButton from "../components/MainButton";
import BuyTicketIcon from "../assets/icons/buy-ticket-icon.svg";
import {Modal, Spin} from "antd";
import TonIcon from "../assets/icons/ton-icon.svg";
import DecrementIcon from "../assets/icons/decrement-icon.svg";
import IncrementIcon from "../assets/icons/increment-icon.svg";
import {AllTicketsCount} from "../constants/const";
import CompleteModal from "../components/CompleteModal";
import {useTonConnectUI} from "@tonconnect/ui-react";
import ProgressChartMain from "../components/ProgressChartMain";
import {buyMegaJackpotTicket, getMegaJackpotInfo, getMegaTicketPriceNano} from "../blockchain/lotteryClient";
import {fromNano} from "@ton/core";
import { sendAdsgramConversion } from "../utils/adsgram";
import { prepareTonPurchaseTracking, trackTonPurchase } from "../utils/metaPixel";

function MegaJackpotPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inputValue, setInputValue] = useState(1);
    const [megaTicketPriceNano, setMegaTicketPriceNano] = useState<bigint | null>(null);
    const [isMegaPriceLoading, setIsMegaPriceLoading] = useState(false);
    const [soldTicketsCount, setSoldTicketsCount] = useState(0);
    const [totalTicketsCount, setTotalTicketsCount] = useState(AllTicketsCount);
    
    // ✅ Main loading state (full-screen spinner)
    const [loading] = useState<any>(false);
    
    // ✅ Specific loading state for button (during purchase only)
    const [isBuyingMegaTicket, setIsBuyingMegaTicket] = useState(false);
    
    // ✅ Error handling
    const [isError, setIsError] = useState(false);
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [tonConnectUI] = useTonConnectUI();
    const navigate = useNavigate();

    const [percent, setPercent] = useState(0);
    const [price, setPrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [megaJackpotStart] = useState(false);

    const soldTicketsPercent = useMemo(() => {
        if (totalTicketsCount <= 0) return 0;
        const currentPercent = (soldTicketsCount / totalTicketsCount) * 100;
        return Math.min(Math.max(currentPercent, 0), 100);
    }, [soldTicketsCount, totalTicketsCount]);

    const soldTicketsPercentLabel = useMemo(() => {
        return `${Math.round(soldTicketsPercent)}% заполнено`;
    }, [soldTicketsPercent]);

    useEffect(() => {
        setPrice(13120)
        setTotalPrice(25000)
        setPercent(13120 / 25000)
    }, [])

    useEffect(() => {
        const loadMegaJackpotStats = async () => {
            try {
                const megaJackpotInfo = await getMegaJackpotInfo();

                if (!megaJackpotInfo) {
                    return;
                }

                setSoldTicketsCount(megaJackpotInfo.ticketsSold);
                setTotalTicketsCount(megaJackpotInfo.ticketsRequired || AllTicketsCount);
            } catch (error) {
                console.error("[MegaJackpot] Failed to load ticket stats:", error);
            }
        };

        loadMegaJackpotStats();
    }, []);

    useEffect(() => {
        if (!isModalOpen) return;

        let cancelled = false;

        const loadMegaTicketPrice = async () => {
            try {
                setIsMegaPriceLoading(true);
                const priceNano = await getMegaTicketPriceNano();

                if (!cancelled) {
                    setMegaTicketPriceNano(priceNano);
                }
            } catch (error) {
                console.error("[MegaJackpot] Failed to load mega ticket price:", error);
                if (!cancelled) {
                    setMegaTicketPriceNano(null);
                }
            } finally {
                if (!cancelled) {
                    setIsMegaPriceLoading(false);
                }
            }
        };

        loadMegaTicketPrice();

        return () => {
            cancelled = true;
        };
    }, [isModalOpen]);

    const totalPriceNano = useMemo(() => {
        if (!megaTicketPriceNano) return null;
        if (!inputValue || inputValue < 1) return 0n;
        return megaTicketPriceNano * BigInt(inputValue);
    }, [megaTicketPriceNano, inputValue]);

    const totalPriceTon = useMemo(() => {
        if (totalPriceNano === null) return "--";

        const tonString = fromNano(totalPriceNano);
        if (!tonString.includes(".")) return tonString;

        const [whole, decimalsRaw] = tonString.split(".");
        const decimalsTrimmed = decimalsRaw.replace(/0+$/, "");
        return decimalsTrimmed.length > 0 ? `${whole}.${decimalsTrimmed}` : whole;
    }, [totalPriceNano]);

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleOpenBuyMegaJackpotModal = () => {
        if (tonConnectUI.connected) {
            setIsModalOpen(true)
        } else {
            tonConnectUI.openModal()
                .then(() => {
                    const checkConnectionInterval = setInterval(() => {
                        if (tonConnectUI.connected === true) {
                            setIsModalOpen(true);
                            clearInterval(checkConnectionInterval);
                        }
                    }, 500);
                })
                .catch((error) => {
                    console.error("Failed to open wallet modal:", error);
                    setIsError(true);
                    setInfoModalOpen(true);
                });

        }
    };

    const handleDecrement = () => {
        if (Number(inputValue) > 1) {
            let decrementValue: any = Number(inputValue) - 1;
            setInputValue(decrementValue);
        }
    };
    const handleIncrement = () => {
        if (Number(inputValue) < AllTicketsCount) {
            let incrementValue: any = Number(inputValue) + 1;
            setInputValue(incrementValue);
        }
    };

    const handleInputValue = (e: any) => {
        let inputCount: any = Number(e.target.value)
        if (inputCount <= AllTicketsCount && inputCount >= 0) {
            setInputValue(inputCount)
        }
    };

    /**
     * ✅ Production-ready Mega Jackpot ticket purchase handler
     * 
     * Features:
     * - Prevents double-click via isBuyingMegaTicket flag
     * - Checks wallet connection
     * - Handles user cancel (soft error, not fatal)
     * - Shows meaningful error messages
     * - Properly formatted transaction amount
     * - Try/catch/finally for cleanup
     * - Loading state during transaction
     */
    const handleBuyMegaJackpotTicket = async () => {
        // ✅ Prevent double-click and re-entry
        if (isBuyingMegaTicket) {
            console.warn("Purchase already in progress");
            return;
        }

        // ✅ Validate input
        if (!inputValue || inputValue < 1) {
            setIsError(true);
            setInfoModalOpen(true);
            return;
        }

        // ✅ Check wallet connection
        if (!tonConnectUI.connected) {
            console.warn("Wallet not connected, opening modal...");
            tonConnectUI.openModal();
            return;
        }

        // Close the modal and show loading
        setIsModalOpen(false);
        setIsBuyingMegaTicket(true);
        setIsError(false);

        try {
            console.log(`[MegaJackpot] Starting purchase: ${inputValue} tickets`);
            const ticketsCount = inputValue;
            prepareTonPurchaseTracking();
            const result = await buyMegaJackpotTicket(tonConnectUI, ticketsCount);

            console.log("[MegaJackpot] Transaction sent:", result);

            const megaJackpotInfo = await getMegaJackpotInfo();
            if (megaJackpotInfo) {
                setSoldTicketsCount(megaJackpotInfo.ticketsSold);
                setTotalTicketsCount(megaJackpotInfo.ticketsRequired || AllTicketsCount);
            }

            // ✅ Success - show completion modal
            setIsError(false);
            setInfoModalOpen(true);
            sendAdsgramConversion().catch(() => {});

            const paidTicketPriceNano = megaTicketPriceNano ?? megaJackpotInfo?.ticketPrice;
            if (paidTicketPriceNano) {
                trackTonPurchase({
                    amountTon: Number(paidTicketPriceNano * BigInt(ticketsCount)) / 1e9,
                    contentName: "mega_jackpot_ticket",
                    contentIds: ["mega-jackpot"],
                    numItems: ticketsCount,
                    transactionId: result?.boc,
                }).catch(() => {});
            }

            // ✅ Optional: Reset form after short delay
            setTimeout(() => {
                setInputValue(1);
            }, 1000);

        } catch (error: any) {
            console.error("[MegaJackpot] Purchase error:", error);

            // ✅ Distinguish between user cancel and actual error
            const errorMessage = error?.message || "";

            // User cancelled wallet confirmation (not a fatal error)
            if (
                errorMessage.includes("cancelled") ||
                errorMessage.includes("rejected") ||
                errorMessage.includes("User cancelled") ||
                error?.code === 0
            ) {
                console.info("[MegaJackpot] User cancelled the transaction");
                // Don't show error modal for user cancel
                setIsBuyingMegaTicket(false);
                return;
            }

            // Actual error - show to user
            setIsError(true);
            setInfoModalOpen(true);

        } finally {
            // ✅ Always cleanup loading state
            setIsBuyingMegaTicket(false);
        }
    };

    return (
        <>
            {loading ? <Spin fullscreen={true} size="large"/> :
                <div className="jackpot-info-card">
                    <div className="jackpot-header">
                        <img
                            className="jackpot-header_left-arrow"
                            src={leftArrow}
                            alt="icon"
                            width={32}
                            height={32}
                            onClick={() => navigate('/games')}
                        />
                        <span>Мега Джекпот</span>
                    </div>

                    {megaJackpotStart ?
                        <div className="mega-jackpot-info-card">
                            <div className="mega-jackpot-info-card_title">
                                В ожидании запуска
                            </div>
                            <ProgressChartMain percent={percent} price={price} totalPrice={totalPrice}/>
                            <div className="mega-jackpot-info-card_content">
                                Как только баланс достигнет $25,000 соревнование начнётся. Следите за прогрессом на
                                шкале и
                                будьте готовы к большому выигрышу!
                            </div>

                        </div> :
                        <div className="mega-jackpot-start-card">
                            <div className="mega-jackpot-start-card_content">
                                <div className="mega-jackpot-start-card_content__title">
                                    Выигрыш
                                </div>
                                <div className="mega-jackpot-start-card_content__count">
                                    {"48,211"} TON
                                </div>
                                <div className="mega-jackpot-start-card_tickets-card">
                                    <div className="mega-jackpot-start-card_tickets-card__title">
                                        ДЖЕКПОТ
                                    </div>
                                    <div className="mega-jackpot-start-card_tickets-card__subtitle">
                                        Продано билетов:
                                    </div>
                                    <div className="mega-jackpot-start-card_tickets-card__numbers">
                                        <span className="mega-jackpot-start-card_tickets-card__numbers-sold">
                                            {soldTicketsCount}
                                        </span>
                                        <span className="mega-jackpot-start-card_tickets-card__numbers-total">
                                            /{totalTicketsCount}
                                        </span>
                                    </div>
                                    <div className="mega-jackpot-start-card_tickets-card__progress">
                                        <div
                                            className="mega-jackpot-start-card_tickets-card__progress-fill"
                                            style={{width: `${soldTicketsPercent}%`}}
                                        />
                                    </div>
                                    <div className="mega-jackpot-start-card_tickets-card__footer">
                                        {soldTicketsPercentLabel}
                                    </div>
                                </div>
                                <div className="mega-jackpot-start-card_content__btn">
                                    <MainButton
                                        icon={BuyTicketIcon}
                                        text={'Купить билет'}
                                        func={handleOpenBuyMegaJackpotModal}
                                    />
                                </div>
                            </div>
                        </div>
                    }


                    <Modal
                        title="Мега Джекпот"
                        open={isModalOpen}
                        onCancel={handleCancel}
                        footer={null}
                        rootClassName='buy-ticket-modal'
                    >
                        <div className="buy-ticket-modal-content">
                            <div className="buy-ticket-modal-content_price">
                                <img src={TonIcon} alt="icon" width={40} height={40}/>
                                <span>{isMegaPriceLoading ? "-- TON" : `${totalPriceTon} TON`}</span>
                            </div>
                            <div className="datetime-container"
                                 style={{height: "auto", marginTop: "16px", marginBottom: '32px'}}>
                                <div className="buy-ticket-block">
                                    <div className="buy-ticket-block_header">
                                        <div className="buy-ticket-block_header__title">
                                            Количество билетов
                                        </div>
                                        <div className="buy-ticket-block_header__subtitle">
                                            Вы можете выбрать количество билетов с номерами, которые будут присвоены
                                            случайным образом.
                                        </div>
                                    </div>
                                    <div className="ticket-count-input">
                                        <img src={DecrementIcon} alt="icon" onClick={handleDecrement}/>
                                        <input
                                            type="text"
                                            className="ticket-count-input_count"
                                            value={inputValue}
                                            onChange={handleInputValue}
                                            disabled={isBuyingMegaTicket}
                                        />
                                        <img src={IncrementIcon} alt="icon" onClick={handleIncrement}/>
                                    </div>

                                    <div className="buy-ticket-block_btn">
                                        <MainButton
                                            icon={BuyTicketIcon}
                                            text={isBuyingMegaTicket ? 'Покупка...' : (isMegaPriceLoading ? 'Купить за -- TON' : `Купить за ${totalPriceTon} TON`)}
                                            func={handleBuyMegaJackpotTicket}
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>

                    </Modal>

                    <CompleteModal
                        infoModalOpen={infoModalOpen}
                        setInfoModalOpen={setInfoModalOpen}
                        isError={isError}
                    />
                </div>
            }
        </>
    );
}


export default MegaJackpotPage